<template>
<select class="wn-choices"
    ref="main"
    :value="selectedValue"
    @change="handleValue"
    :multiple="multiple">
    <option value="">{{ placeholder || $_locale('choicesjs.noValue') }}</option>
    <template v-if="groups.length">
        <optgroup
            v-for="group in groups"
            :key="group.label"
            :label="group.label">
            <option
                v-for="option in group.options"
                :key="option.id"
                :value="option.id">
                {{ option.text }}
            </option>
        </optgroup>
    </template>
    <template v-else>
        <option
            v-for="option in options"
            :key="option.id"
            :value="option.id">
            {{ option.text }}
        </option>
    </template>
</select>
</template>

<script>
import Choices from 'choices.js'
import 'choices.js/public/assets/styles/choices.min.css'

export default {
    emits: ['input'],
    props: {
        options: {
            type: Array,
            required: false,
            default: () => ([]),
        },
        groups: {
            type: Array,
            required: false,
            default: () => ([]),
        },
        value: {
            required: false,
        },
        multiple: {
            type: Boolean,
            required: false,
            default: false,
        },
        settings: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        placeholder: {
            type: String,
            required: false,
        },
    },
    data: () => ({
        instance: null,
        selectedValue: null,
    }),
    watch: {
        options: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this._destroyInstance()
                    this.init()
                })
            }
        },
        groups: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this._destroyInstance()
                    this.init()
                })
            }
        },
        value(newVal) {
            const id = newVal?.id ?? null
            if (id !== null && this.instance) {
                this.instance.setChoiceByValue(String(id))
            }
        },
    },
    methods: {
        _destroyInstance() {
            if (this.instance) {
                this.instance.destroy()
                this.instance = null
            }
        },
        init() {
            const { main } = this.$refs
            const choicesSettings = {
                shouldSort: false,
                allowHTML: true,
                noResultsText: this.$_locale('choicesjs.noResultsText'),
                noChoicesText: this.$_locale('choicesjs.noChoicesText'),
                itemSelectText: this.$_locale('choicesjs.itemSelectText'),
            }

            if (this.settings) {
                for (let key of Object.keys(this.settings)) {
                    choicesSettings[key] = this.settings[key]
                }
            }

            const hasContent = this.options.length > 0 || this.groups.length > 0
            if (hasContent) {
                this.instance = new Choices(main, choicesSettings)

                // Suppress iOS autofill candidate generation to prevent UIKeyboardTaskQueue
                // hang when user double-taps the cloned input inside WKWebView
                const clonedInput = main.closest('.choices')?.querySelector('.choices__input--cloned')
                if (clonedInput) {
                    clonedInput.setAttribute('autocomplete', 'off')
                    clonedInput.setAttribute('autocorrect', 'off')
                    clonedInput.setAttribute('autocapitalize', 'off')
                    clonedInput.setAttribute('spellcheck', 'false')
                }

                // Apply value prop if it was set before the instance was ready
                const valueId = this.value?.id ?? null
                if (valueId !== null) {
                    this.instance.setChoiceByValue(String(valueId))
                    this.selectedValue = valueId
                }
            }
        },
        handleValue($event) {
            const allOptions = this.groups.length
                ? this.groups.flatMap(g => g.options)
                : this.options

            if (this.multiple) {
                const selectedOptions = Array.from(
                    $event.target.selectedOptions
                ).map(option => {
                    const [ selected ] = allOptions.filter(row => row.id == option.value)
                    return selected
                })

                this.$emit('input', selectedOptions)
                return;
            }

            this.selectedValue = $event.target.value

            const [ selected ] = allOptions.filter(row => row.id == this.selectedValue)

            this.$emit('input', selected || null)
        },
        getInstance() {
            return this.instance
        },
        setChoiceByValue(val) {
            if (this.instance) {
                this.instance.setChoiceByValue(String(val))
            }
        },
    },
    mounted() {
        if (this.value?.id) {
            this.selectedValue = this.value.id
        }

        this.$nextTick(() => this.init())
    },
    beforeUnmount() {
        this._destroyInstance()
    },
}
</script>
